import styled from 'styled-components'
import {Input, Row, Text, theme} from '../../ui'
import {Button} from '@material-ui/core'

export const ModalText = styled(Text)`
  color: ${theme.color.base};
  font-weight: 500;
  font-size: 15px;
`

// The red asterisk that marks a required field, for the labels that are plain
// text rather than a Material-UI input label (those get theirs from the
// `required` prop - see .MuiFormLabel-asterisk in GlobalStyles).
export const RequiredMark = styled.span`
  color: #DC2626;
  margin-right: 2px;
  font-weight: 500;
`

export const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 6px 4px;
  white-space: nowrap;
  gap: 8px;
`

export const ToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  width: 100%;
  margin: 6px 0 4px;
`

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex: 0 1 264px;
  min-width: 220px;
  white-space: nowrap;
  background: ${theme.color.whiteBackground};
  border: 1px solid ${theme.color.lightGrayBackground};
  border-radius: 10px;
  padding: 11px 16px;
`

export const ConfirmButton = styled(Button)`
  &.MuiButtonBase-root {
    font-size: ${theme.fontSize.normal};
    font-weight: 600;
    min-width: 140px;
    height: 46px;
    margin: 0.4rem;
    border-radius: 10px;
    box-shadow: 0 4px 12px ${theme.color.confirmButton}33;
    text-transform: none;
    background: ${theme.color.confirmButton};
    transition: ${theme.css.transition};

    &:hover {
      background: ${theme.color.darkBlue};
      box-shadow: 0 6px 16px ${theme.color.confirmButton}4d;
    }

    color: ${theme.color.white};

    &.Mui-disabled {
      opacity: 0.4;
      box-shadow: none;
    }
  }
`

export const DeleteButton = styled(Button)`
  &.MuiButtonBase-root {
    font-size: ${theme.fontSize.normal};
    font-weight: 600;
    min-width: 140px;
    height: 46px;
    margin: 0.4rem;
    border-radius: 10px;
    text-transform: none;
    background: ${theme.color.red};
    transition: ${theme.css.transition};

    &:hover {
      background: ${theme.color.dangerRedLight};
    }

    color: ${theme.color.white};

    &.Mui-disabled {
      opacity: 0.4;
    }
  }
`

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 4px;
`

export const ConfirmButtonSm = styled(Button)`
  &.MuiButtonBase-root {
    font-size: 14px;
    font-weight: 600;
    min-width: 96px;
    height: 38px;
    margin: 0;
    padding: 0 18px;
    border-radius: 9px;
    text-transform: none;
    box-shadow: 0 3px 10px ${theme.color.confirmButton}2e;
    background: ${theme.color.confirmButton};
    transition: ${theme.css.transition};

    &:hover {
      background: ${theme.color.darkBlue};
      box-shadow: 0 5px 14px ${theme.color.confirmButton}45;
    }

    color: ${theme.color.white};

    &.Mui-disabled {
      opacity: 0.4;
      box-shadow: none;
    }
  }
`

export const DeleteButtonSm = styled(Button)`
  &.MuiButtonBase-root {
    font-size: 14px;
    font-weight: 600;
    min-width: 92px;
    height: 38px;
    margin: 0;
    padding: 0 16px;
    border-radius: 9px;
    text-transform: none;
    background: transparent;
    border: 1.5px solid ${theme.color.red}66;
    transition: ${theme.css.transition};

    &:hover {
      background: ${theme.color.red}12;
      border-color: ${theme.color.red};
    }

    color: ${theme.color.red};

    &.Mui-disabled {
      opacity: 0.4;
      border-color: ${theme.color.gray};
      color: ${theme.color.grey};
    }
  }
`

export const ModalRow = styled(Row)`
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`

export const DialogHeaderText = styled.p`
  font-size: ${theme.fontSize.large};
  font-weight: 700;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.3;
  letter-spacing: normal;
  text-align: right;
  color: ${theme.color.mekomiBlack};
  margin: 0;
  padding-right: 12px;
  border-right: 4px solid ${theme.color.confirmButton};

  body[dir='ltr'] & {
    text-align: left;
    padding-right: 0;
    padding-left: 12px;
    border-right: none;
    border-left: 4px solid ${theme.color.confirmButton};
  }
`

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
`

export const OutlinedInput = styled(Input)`
  border-radius: 8px;
  width: 100%;

  .MuiOutlinedInput-root {
    border-radius: 8px;
  }

  .MuiOutlinedInput-notchedOutline {
    border: solid 1.5px ${theme.color.inputGrayBorder};
    border-radius: 8px;
    transition: ${theme.css.transition};
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.confirmButton}80;
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.confirmButton};
    border-width: 1.5px;
  }

  background-color: ${theme.color.white};
`

export const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`

export const InputErrorText = styled.span`
  position: absolute;
  right: 6px;
  bottom: -18px;
  color: ${theme.color.dangerRed};
  font-size: 12px;

  body[dir='ltr'] & {
    right: unset;
    left: 6px;
  }
`

// Shown in place of a section's controls when the module it configures has
// not been added to the community yet.
export const EmptyState = styled.div`
  display: flex;
  padding: 24px 4px;
  color: ${theme.color.grey};
  font-size: 14px;
  line-height: 22px;
  text-align: right;
`

export const InputWrapper = styled(InfoWrapper)`
  max-width: 340px;
  width: 100%;
`

// A url gets the full width of the card rather than the 340px a name needs,
// and wraps onto a second line when even that is not enough - it has no spaces
// to break at, so break-all is what lets the whole address be read at once.
export const UrlInputWrapper = styled(InfoWrapper)`
  width: 100%;
  align-items: flex-start;
`

export const UrlInput = styled(OutlinedInput)`
  .MuiOutlinedInput-root {
    align-items: flex-start;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  textarea {
    word-break: break-all;
    line-height: 1.45;
  }
`
